import List from "rc-virtual-list"
import { useRef } from "react"

import { Checkbox, Text } from "@/atoms"
import { ANALYTIC_EVENTS } from "@/const/event.constants"
import { hasShouldFetchFilterOption } from "@/helpers/filters.helper"
import { useTranslation } from "@/hooks/useTranslation"
import { Table } from "@/icons"
import { getSortLabel } from "@/organisms/SmartSearch/helpers/option.helpers"
import { ItemListTemplate } from "../../templates/ItemListTemplate"
import { DescriptionItem } from "../DescriptionItem"

export const ValueList = (props: any) => {
  const { logger, services, createHandler } = props
  const { FilterService, StringService } = services
  const currentToken = StringService.getTokenByCaretPos()
  const filter = FilterService.getFilterByLabel(currentToken?.id)
  const values: string[] = (currentToken?.value || "").split(",")
  const lastValue = values[values.length - 1]
  const { type, options = [], order } = filter || {}
  const descriptionItemClassName =
    "item-smart-search-ks value-item-smart-search-ks"
  const { t } = useTranslation()

  const tokenCurrent = useRef<typeof currentToken>(currentToken)
  const optionsCurrent = useRef(null)

  if (optionsCurrent.current && tokenCurrent.current?.id !== currentToken?.id) {
    optionsCurrent.current = options
    tokenCurrent.current = currentToken
  }

  let optionsByCurrentValue = optionsCurrent.current || options

  // Show loader while options are being fetched from API
  if (hasShouldFetchFilterOption(optionsByCurrentValue)) {
    return (
      <ItemListTemplate loading title="values" Icon={Table}>
        <div className="value-list-loader-text-ks">
          <Text type="secondary">
            {t("filters.helpers.loadingFilterOptions")}
          </Text>
        </div>
      </ItemListTemplate>
    )
  }
  if (lastValue) {
    let searchValue = lastValue.toLocaleLowerCase().replace(/"$/, "")

    if (
      searchValue.length === 2 &&
      searchValue[0] === '"' &&
      searchValue[1] === '"'
    ) {
      searchValue = ""
    }

    optionsByCurrentValue = optionsByCurrentValue.filter(({ label }: any) => {
      return label.toLocaleLowerCase().includes(searchValue)
    })
  }

  if (type === "single") {
    return (
      <ItemListTemplate title="values" Icon={Table}>
        <div className="wrapper-list-value-items-ks">
          {optionsByCurrentValue.map(({ label, value, disabled }: any) => {
            if (disabled) return null

            const handler = (e: MouseEvent | KeyboardEvent) => {
              createHandler(() => {
                logger?.info(
                  ANALYTIC_EVENTS.TAB.FILTER.ADVANCE_SEARCH.CLICK.VALUE,
                  { value, key: currentToken?.id }
                )

                return label
              })(e)
            }

            return (
              <DescriptionItem
                className={descriptionItemClassName}
                key={value}
                handler={handler}
              >
                {label}
              </DescriptionItem>
            )
          })}
        </div>
      </ItemListTemplate>
    )
  }

  if (type === "multiple") {
    const { valueClickedLastTime } = props
    const valuesMap = values.reduce(
      (acc, value) => {
        if (!value) return acc

        acc[value] = true

        return acc
      },
      {} as Record<string, boolean>
    )

    if (!optionsCurrent.current) {
      optionsCurrent.current =
        order === "none"
          ? options
          : options.sort((a: any, b: any) => {
              if (valuesMap[a.label] && valuesMap[b.label])
                return getSortLabel(a.label).localeCompare(
                  getSortLabel(b.label)
                )

              if (valuesMap[a.label]) return -1
              if (valuesMap[b.label]) return 1

              return getSortLabel(a.label).localeCompare(getSortLabel(b.label))
            })
    }
    const itemHeight = 38 // .item-smart-search-ks height (line-height + padding)
    const maxHeightList = 240
    const lengthList = optionsByCurrentValue?.length || 0
    const itemsHeight = lengthList * itemHeight
    const heightList = itemsHeight > maxHeightList ? maxHeightList : itemsHeight

    return (
      <ItemListTemplate title="values" Icon={Table}>
        <List
          itemHeight={itemHeight}
          height={heightList}
          data={optionsByCurrentValue}
          itemKey={(item: any) => item?.value}
        >
          {({ label, value, displayLabel = label }) => {
            const handler = (e: any) => {
              createHandler(() => {
                valueClickedLastTime.current = label
                valuesMap[label] = !valuesMap[label]

                const currentTokenValue = currentToken?.value
                const commaIsLast =
                  !currentTokenValue ||
                  currentTokenValue[currentTokenValue.length] === ","

                if (!commaIsLast) {
                  valuesMap[values[values.length - 1]] = false
                }

                logger?.info(
                  ANALYTIC_EVENTS.TAB.FILTER.ADVANCE_SEARCH.CLICK.VALUE,
                  {
                    label,
                    value: valuesMap[label]
                  }
                )

                return Object.keys(valuesMap).reduce((acc, key) => {
                  if (valuesMap[key]) {
                    acc.push(key)
                  }

                  return acc
                }, [] as string[])
              })(e)
            }

            return (
              <li
                className={descriptionItemClassName}
                key={value}
                tabIndex={label === valueClickedLastTime.current ? 2 : 1}
                onClick={handler}
                onKeyDown={handler}
              >
                <Checkbox checked={valuesMap[label]}>{displayLabel}</Checkbox>
              </li>
            )
          }}
        </List>
      </ItemListTemplate>
    )
  }

  return null
}
